/**
 * Tabbed Media — external asset
 * Handles Tabbed Media sections on initial load, in Shopify Theme Editor,
 * and when sections are dynamically injected.
 */
(function () {
  'use strict';

  var SELECTORS = {
    root: '[data-tbm]',
    tab: '[data-tbm-tab]',
    panel: '[data-tbm-panel]',
    cover: '[data-tbm-cover]',
    player: '[data-tbm-player]',
    panels: '.tbm__panels'
  };

  function TabbedMedia(root) {
    this.root = root;
    this.tabs = [].slice.call(root.querySelectorAll(SELECTORS.tab));
    this.panels = [].slice.call(root.querySelectorAll(SELECTORS.panel));
    this.count = this.tabs.length;
    this.index = 0;
    this._clickHandler = this._onClick.bind(this);
    this._keyHandler = this._onKeyDown.bind(this);
    this._touchStart = this._onTouchStart.bind(this);
    this._touchMove = this._onTouchMove.bind(this);
    this._touchEnd = this._onTouchEnd.bind(this);
    this._touch = { startX: 0, dx: 0, dragging: false };
    this._init();
  }

  TabbedMedia.prototype._init = function () {
    if (this.count === 0) return;
    if (this.root.getAttribute('data-tbm-ready') === 'true') this._destroy();
    this.root.setAttribute('data-tbm-ready', 'true');

    this.root.addEventListener('click', this._clickHandler);
    this.root.addEventListener('keydown', this._keyHandler);
    this._bindSwipe();

    this.go(0, { silent: true, scroll: false });
  };

  TabbedMedia.prototype._destroy = function () {
    this.root.removeEventListener('click', this._clickHandler);
    this.root.removeEventListener('keydown', this._keyHandler);
    this._unbindSwipe();
  };

  TabbedMedia.prototype._bindSwipe = function () {
    this.stage = this.root.querySelector(SELECTORS.panels);
    if (!this.stage) return;
    this.stage.addEventListener('touchstart', this._touchStart, { passive: true });
    this.stage.addEventListener('touchmove', this._touchMove, { passive: true });
    this.stage.addEventListener('touchend', this._touchEnd);
  };

  TabbedMedia.prototype._unbindSwipe = function () {
    if (!this.stage) return;
    this.stage.removeEventListener('touchstart', this._touchStart);
    this.stage.removeEventListener('touchmove', this._touchMove);
    this.stage.removeEventListener('touchend', this._touchEnd);
  };

  TabbedMedia.prototype._onClick = function (event) {
    var tab = event.target.closest && event.target.closest(SELECTORS.tab);
    if (tab && this.root.contains(tab)) {
      event.preventDefault();
      var idx = parseInt(tab.getAttribute('data-tbm-index'), 10);
      this.go(isFinite(idx) ? idx : 0);
      return;
    }

    var cover = event.target.closest && event.target.closest(SELECTORS.cover);
    if (!cover || !this.root.contains(cover)) return;

    var panel = cover.closest(SELECTORS.panel);
    var player = panel && panel.querySelector(SELECTORS.player);
    if (!player) return;

    cover.style.display = 'none';
    player.classList.add('is-visible');

    var video = player.querySelector('video');
    if (video && typeof video.play === 'function') {
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
    }

    var iframe = player.querySelector('iframe');
    if (iframe) {
      var dataSrc = iframe.getAttribute('data-src');
      if (dataSrc && !iframe.getAttribute('src')) {
        iframe.setAttribute('src', dataSrc);
      } else if (iframe.contentWindow) {
        iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":[]}', '*');
      }
    }
  };

  TabbedMedia.prototype._onKeyDown = function (event) {
    var tab = event.target.closest && event.target.closest(SELECTORS.tab);
    if (!tab || !this.root.contains(tab)) return;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.go(this.index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.go(this.index - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.go(0);
        break;
      case 'End':
        event.preventDefault();
        this.go(this.count - 1);
        break;
    }
  };

  TabbedMedia.prototype._onTouchStart = function (e) {
    this._touch.startX = e.touches[0].clientX;
    this._touch.dragging = true;
  };

  TabbedMedia.prototype._onTouchMove = function (e) {
    if (!this._touch.dragging) return;
    this._touch.dx = e.touches[0].clientX - this._touch.startX;
  };

  TabbedMedia.prototype._onTouchEnd = function () {
    if (!this._touch.dragging) return;
    if (Math.abs(this._touch.dx) > 40) {
      this.go(this.index + (this._touch.dx < 0 ? 1 : -1));
    }
    this._touch.dragging = false;
    this._touch.dx = 0;
  };

  TabbedMedia.prototype._stopVideos = function () {
    this.panels.forEach(function (panel) {
      var player = panel.querySelector(SELECTORS.player);
      var cover = panel.querySelector(SELECTORS.cover);

      if (player) {
        player.classList.remove('is-visible');

        var iframe = player.querySelector('iframe');
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":[]}', '*');
        }

        var video = player.querySelector('video');
        if (video && typeof video.pause === 'function') {
          try { video.pause(); } catch (e) {}
        }
      }

      if (cover) {
        cover.style.display = '';
      }
    });
  };

  TabbedMedia.prototype.go = function (next, options) {
    options = options || {};
    this.index = (next % this.count + this.count) % this.count;

    var self = this;
    this.tabs.forEach(function (tab, i) {
      var selected = i === self.index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;

      if (selected && options.scroll !== false && typeof tab.scrollIntoView === 'function') {
        try {
          tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        } catch (e) {
          tab.scrollIntoView({ inline: 'center', block: 'nearest' });
        }
      }
    });

    this.panels.forEach(function (panel, i) {
      panel.classList.toggle('is-active', i === self.index);
    });

    this._stopVideos();
  };

  function initSection(root) {
    if (!root || root.getAttribute('data-tbm-ready') === 'true') return;
    new TabbedMedia(root);
  }

  function initAll(scope) {
    scope = scope || document;
    [].slice.call(scope.querySelectorAll(SELECTORS.root)).forEach(initSection);
  }

  function handleSectionLoad(event) {
    var target = event.target;
    if (!target) return;
    var root = target.matches && target.matches(SELECTORS.root)
      ? target
      : target.querySelector && target.querySelector(SELECTORS.root);
    if (root) initSection(root);
  }

  function handleBlockSelect(event) {
    var panel = event.target.closest && event.target.closest(SELECTORS.panel);
    if (!panel) return;
    var root = panel.closest(SELECTORS.root);
    if (!root) return;
    var tab = root.querySelector(SELECTORS.tab + '[data-tbm-index="' + panel.getAttribute('data-tbm-index') + '"]');
    if (tab) tab.click();
  }

  function observeMutations() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        [].slice.call(mutation.addedNodes).forEach(function (node) {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches && node.matches(SELECTORS.root)) initSection(node);
          var children = node.querySelectorAll && node.querySelectorAll(SELECTORS.root);
          if (children) [].slice.call(children).forEach(initSection);
        });
      });
    });

    var target = document.body || document.documentElement;
    if (target) {
      observer.observe(target, { childList: true, subtree: true });
    }
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:block:select', handleBlockSelect);

  if (typeof MutationObserver !== 'undefined') {
    if (document.body) {
      observeMutations();
    } else {
      document.addEventListener('DOMContentLoaded', observeMutations);
    }
  }
})();
